import { hasFocusClass, postContentClass } from "@repo/fika-blog/src/classes"
import { postClass, postContainerClass } from "@repo/fika-blog/src/content.css"
import { extensions } from "@repo/fika-blog/src/extensions"
import { NodeTypeExtension } from "@repo/fika-blog/src/extensions/nodeType"
import type { JSONContent } from "@repo/fika-blog/src/tiptap/core"
import type { Replicache } from "@rocicorp/replicache"
import { throttle } from "@solid-primitives/scheduled"
import { useParams } from "@solidjs/router"
import TiptapFileHandler from "@tiptap/extension-file-handler"
import Focus from "@tiptap/extension-focus"
import TiptapUniqueID from "@tiptap/extension-unique-id"
import { isDeepEqual } from "remeda"
import { createEffect, createMemo, Show, untrack } from "solid-js"
import { createTiptapEditor } from "solid-tiptap"
import { useAppContext } from "@/context/app"
import type { M } from "@/data/mutators"
import { Box } from "@/designSystem/components/box"
import { LinkButton } from "@/designSystem/components/button"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { ButtonDrawer } from "@/designSystem/patterns/button-drawer"
import { Navigation } from "@/designSystem/patterns/navigation"
import { useI18nContext } from "@/i18n/i18n-solid"
import { defaultJsonContent } from "@/lib/defaultJsonContent"
import { createBlogRouter, type SectionName } from "@/routes/posts-router"
import { PanelMenu } from "./editor/menus/panelMenu"
import { Placeholder } from "./editor/nodes/placeholder"
import { RightPanel } from "./editor/rightPanel"
import { onDrop, onPaste } from "./image-upload"
import {
  clickAreaClass,
  dropCursorClass,
  editorClass,
} from "./post-section.css"

type UpdateForm = {
  replicache: Replicache<M>
  userId: string
  json: string
  attributeName: string
}
const updateForm = throttle(
  ({ replicache, userId, json, attributeName }: UpdateForm) => {
    replicache.mutate.updateUser({
      id: userId,
      [attributeName]: json,
    })
  },
  1_000,
)

export function PostSection() {
  const { user, replicache } = useAppContext()
  const params = useParams()
  const sectionName = () => params.sectionName as SectionName

  const { paths } = createBlogRouter()

  let editorRef!: HTMLDivElement

  const attributeName = createMemo(() => `blog_${sectionName()}` as const)
  const postJSON = createMemo<JSONContent>(() =>
    JSON.parse(user[attributeName()] || defaultJsonContent),
  )

  const getEditor = createTiptapEditor(() => ({
    element: editorRef,
    content: untrack(() => postJSON()),
    onTransaction() {
      const instance = getEditor()
      if (!instance) return

      const json = instance.getJSON()
      if (isDeepEqual(json, postJSON())) return

      updateForm({
        replicache,
        userId: user.id,
        json: JSON.stringify(json),
        attributeName: attributeName(),
      })
    },
    editorProps: {
      attributes: {
        class: `${editorClass} ${postContentClass}`,
      },
    },
    extensions: [
      ...extensions({
        dropcursor: {
          // @ts-ignore
          color: false,
          width: 2,
          class: dropCursorClass,
        },
      }),
      TiptapUniqueID.configure({
        attributeName: "uid",
        types: ["image"],
      }),
      Placeholder,
      Focus.configure({ className: hasFocusClass, mode: "deepest" }),
      TiptapFileHandler.configure({ onDrop, onPaste }),
      NodeTypeExtension,
    ],
  }))

  function addParagraph() {
    const editor = getEditor()
    if (!editor) return

    const lastPos = editor.state.doc.content.size
    editor.chain().focus().insertContentAt(lastPos, { type: "paragraph" }).run()
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      addParagraph()
    }
  }

  createEffect(() => {
    const editor = getEditor()
    if (!editor) return

    const json = editor.getJSON()
    if (isDeepEqual(postJSON(), json)) return

    const imgPositions: { pos: number; attributes: any }[] = []
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === "image") {
        imgPositions.push({
          pos,
          attributes: node.attrs,
        })
      }
    })

    const imgs: Record<string, string> = {}

    function traverse(node: any) {
      if (node.type === "image") {
        imgs[node.attrs.uid] = node.attrs.src
      }

      if (node.content && Array.isArray(node.content)) {
        node.content.forEach(traverse)
      }
    }

    traverse(postJSON())

    for (const pos of imgPositions) {
      const src = imgs[pos.attributes.uid]
      if (!src) continue
      if (src === pos.attributes.src) continue

      // https://x.com/rauchg/status/1805274417158824431
      const img = new Image()
      img.onload = () => {
        const oldSelection = editor.state.selection
        editor
          .chain()
          .setNodeSelection(pos.pos)
          .updateAttributes("image", { src })
          .setTextSelection(oldSelection.from)
          .run()
      }
      img.src = src
    }
  })
  const { LL } = useI18nContext()
  const buttons = createMemo(() => {
    return [
      {
        label: LL().common.blog(),
        href: paths().url,
      } as const,
      {
        label: "blog settings",
        href: paths().settings().url,
      } as const,
    ]
  })

  return (
    <MainLayout
      header={
        <Box gap="12">
          <Navigation hue="amber" buttons={buttons()} title={sectionName()} />
          <PanelMenu editor={getEditor()} />
        </Box>
      }
      footer={
        <ButtonDrawer
          hue="amber"
          rightActions={
            <LinkButton
              hue="amber"
              iconName="arrow-left"
              kind="darkGhost"
              href={paths().settings().url}
            >
              go back
            </LinkButton>
          }
        />
      }
      rightPanel={
        <Show when={getEditor()}>
          {editor => <RightPanel editor={editor()} />}
        </Show>
      }
    >
      <div class={postContainerClass}>
        <div class={postClass}>
          <div>
            <div ref={editorRef} />
            <Show when={!getEditor()?.isEmpty}>
              <div
                class={clickAreaClass}
                onClick={addParagraph}
                onKeyDown={handleKeyDown}
                onFocus={addParagraph}
                role="button"
                tabIndex={0}
              />
            </Show>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
